import { useEffect, useMemo, useState } from "react"
import petrolImg from "../assets/petrol.png"

const visibleCardCount = 5
const autoSlideDelay = 3000

const defaultItems = [
  {
    number: "01",
    title: "Real-Time Fuel Monitoring",
    desc: "Track fuel levels across all machinery in real time using advanced sensors and GSM-enabled GPS devices.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
  {
    number: "02",
    title: "Fuel Theft Prevention",
    desc: "Get instant alerts for sudden drops, unauthorized fuel drains, or refueling anomalies before they impact your bottom line.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
  {
    number: "03",
    title: "Equipment Location Tracking",
    desc: "Know exactly where your machines are, how long they're running, and if they're idling unnecessarily across construction zones.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
  {
    number: "04",
    title: "Detailed Fuel Reports",
    desc: "Access automated reports showing fuel filled, consumed, and remaining to track usage trends and spot irregularities.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
  {
    number: "05",
    title: "Rugged Hardware",
    desc: "Use weatherproof, dust-resistant sensors built for harsh road construction site environments.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
  {
    number: "06",
    title: "Cloud Dashboard",
    desc: "Monitor all sites from one dashboard with maps, reports, graphs, and fuel consumption logs.",
    image: petrolImg,
    linkText: "Learn more",
    href: "/services",
  },
]

function ArrowButton({ children, onClick, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#df8b2d] bg-[#df8b2d] text-[32px] font-medium leading-none text-white shadow-sm transition-all duration-200 hover:bg-white hover:text-[#df8b2d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#df8b2d]/40"
    >
      {children}
    </button>
  )
}

function SolutionCard({ item, expanded, onActivate }) {
  return (
    <button
      type="button"
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      className={`group relative h-full shrink-0 snap-center overflow-hidden rounded-lg border border-[#e8e1d9] text-left transition-[width,flex-grow,transform] duration-500 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#df8b2d] ${
        expanded
          ? "w-[calc(100vw-48px)] max-w-[350px] bg-white sm:w-[400px] sm:max-w-none md:w-[460px] lg:w-auto lg:flex-[2.8_1_0]"
          : "hidden w-[82px] bg-[linear-gradient(180deg,#fffaf4_0%,#f4c17d_52%,#df8b2d_100%)] sm:block sm:w-[105px] md:w-[115px] lg:w-auto lg:flex-[0.85_1_0]"
      }`}
    >
      {expanded ? (
        <div className="flex h-full flex-col p-2.5 sm:p-3">
          <img
            src={item.image || petrolImg}
            alt={item.title}
            className="h-[145px] w-full rounded-md bg-white object-cover sm:h-[175px] md:h-[190px] lg:h-[205px]"
            onError={(event) => {
              event.currentTarget.onerror = null
              event.currentTarget.src = petrolImg
            }}
          />

          <div className="flex min-h-0 flex-1 flex-col pt-3">
            <div className="flex items-start gap-2">
              <span className="text-lg font-semibold leading-none text-[#df8b2d]">
                {item.number}
              </span>

              <h3 className="text-sm font-semibold leading-5 text-[#111111] sm:text-base">
                {item.title}
              </h3>
            </div>

            <p className="mt-2 text-[10px] leading-[1.65] text-[#4e4741] sm:text-[11px] md:text-[12px]">
              {item.desc}
            </p>

            {item.linkText ? (
              <a
                href={item.href || "/services"}
                className="mt-auto inline-flex items-center gap-2 pt-2 text-[11px] font-medium text-[#df8b2d]"
              >
                {item.linkText}
                <span aria-hidden="true">→</span>
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col px-2.5 py-3 sm:px-3 sm:py-4">
          <span className="text-lg font-semibold leading-none text-[#111111] sm:text-xl">
            {item.number}
          </span>

          <span className="mt-2 h-px w-full bg-[#4b3f37]/50" />

          <div className="flex flex-1 items-end justify-center pb-1">
            <span className="rotate-180 text-center text-[16px] font-semibold leading-5 text-[#111111] [writing-mode:vertical-rl] sm:text-[15px] sm:leading-6">
              {item.title}
            </span>
          </div>
        </div>
      )}
    </button>
  )
}

export default function WhyChooseSolution({ title = "Why Choose Our Solution", items = defaultItems }) {
  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#111111]">{title}</h2>
          <div className="w-20 h-1 bg-[#df8b2d] mx-auto mt-4"></div>
        </div>

        {/* Grid ki jagah flex ka use kiya */}
        <div className="flex flex-wrap justify-center gap-8">
          {items.map((item, index) => (
            <div
              key={index}
              className="relative h-[350px] w-full max-w-[400px] rounded-3xl overflow-hidden group shadow-lg cursor-pointer"
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                style={{ backgroundImage: `url(${item.image})` }}
              >
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all duration-500" />
              </div>
              
              {/* Text Content */}
              <div className="absolute bottom-0 left-0 p-8 w-full transition-all duration-500 group-hover:bottom-12">
                <span className="text-white/80 font-bold text-sm block mb-2">{item.number}</span>
                <h3 className="text-white text-2xl font-bold mb-4 leading-tight">{item.title}</h3>
                
                <div className="overflow-hidden max-h-0 group-hover:max-h-40 transition-all duration-500 opacity-0 group-hover:opacity-100">
                  <p className="text-white/90 text-sm leading-relaxed mb-4">{item.desc}</p>
                  <a href={item.href} className="text-[#df8b2d] font-bold text-sm hover:underline">{item.linkText} →</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}